/**
 * Shared building blocks for the auth UI (WS-D D2′).
 *
 * Bento has no react-router + no Tailwind (unlike world-cup-v2, the port
 * source), so these are plain components over bento's own `.auth-*` CSS classes
 * (styles.css). Login-only: no cross-view navigation primitive is needed.
 */

import { useState, type ReactNode } from "react";

export function AuthCard({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="auth-card">
      <div className="auth-brand">
        BENT<span className="auth-brand-o">O</span>
      </div>
      <h1 className="auth-title">{title}</h1>
      {subtitle && <p className="auth-subtitle">{subtitle}</p>}
      {children}
      {footer && <div className="auth-footer">{footer}</div>}
    </div>
  );
}

export function AuthField({
  label,
  name,
  type = "text",
  value,
  onChange,
  autoComplete,
  placeholder,
  required = true,
  revealable = false,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  /** Password fields: add an eye button that shows/hides the text, so a
   *  mistyped password is easy to catch (same control as BenCat's sign-in). */
  revealable?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const input = (
    <input
      className={revealable ? "auth-input auth-input-revealable" : "auth-input"}
      name={name}
      type={revealable && revealed ? "text" : type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      placeholder={placeholder}
      required={required}
    />
  );
  return (
    <label className="auth-field">
      <span className="auth-field-label">{label}</span>
      {revealable ? (
        <span className="auth-input-wrap">
          {input}
          <button
            type="button"
            className="auth-reveal"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? "Hide password" : "Show password"}
            aria-pressed={revealed}
            // Out of the tab order: Tab should go email → password → Sign in.
            tabIndex={-1}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </span>
      ) : (
        input
      )}
    </label>
  );
}

// Show/hide icons — the exact inline SVGs BenCat uses (Feather eye / eye-off,
// MIT): no asset request, and they take the button's currentColor.
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function AuthSubmit({ label, pending }: { label: string; pending: boolean }) {
  return (
    <button type="submit" className="auth-submit" disabled={pending}>
      {pending ? "…" : label}
    </button>
  );
}

export function AuthError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="auth-notice auth-notice-error" role="alert">
      {message}
    </div>
  );
}
